import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export interface RepoLensStats {
  totalComponents: number;
  analyzedComponents: number;
  failedComponents: number;
  lastRunAt: string | null;
  lastRunType: string | null;
  stackDistribution: Record<string, number>;
  techRadarSummary: {
    adopt: number;
    trial: number;
    assess: number;
    hold: number;
  };
}

export interface StackItem {
  name: string;
  displayName: string;
  version?: string;
  confidence?: 'high' | 'medium' | 'low';
  evidence?: string[];
  quadrant?: 'languages' | 'frameworks' | 'tools' | 'platforms';
  ring?: 'adopt' | 'trial' | 'assess' | 'hold';
}

export interface ExternalServiceTableItem {
  displayName: string;
  category: string;
  provider?: string;
  confidence: string;
}

export interface AnalysisResult {
  id: string;
  componentRef: string;
  componentName: string;
  systemRef: string | null;
  systemName: string | null;
  domainRef: string | null;
  domainName: string | null;
  repoUrl: string | null;
  repoSlug: string | null;
  architectureSummary: string | null;
  isMonorepo: boolean;
  monorepoType: string | null;
  entryPoints: string[];
  configFiles: string[];
  moduleBoundaries: string[];
  analyzedAt: string;
  analysisDurationMs: number | null;
  errorMessage: string | null;
  stacks: StackItem[];
  externalServices: ExternalServiceTableItem[];
}

export interface AnalysisListResponse {
  items: AnalysisResult[];
  total: number;
  page: number;
  limit: number;
}

export interface TechRadarData {
  quadrants: Array<{
    name: string;
    items: Array<{
      name: string;
      displayName: string;
      ring: string;
      count: number;
    }>;
  }>;
}

export interface ExternalServiceSummaryItem {
  entityRef: string;
  serviceKey: string;
  displayName: string;
  provider?: string;
  category: string;
  componentCount: number;
}

export interface ComponentExternalServiceItem {
  entityRef: string;
  displayName: string;
  category: string;
  provider?: string;
  confidence: 'high' | 'medium' | 'low';
  detectedInFiles: string[];
  linkedAt: string;
  autoLinked: boolean;
}

export interface StackAggregation {
  stackName: string;
  displayName: string;
  count: number;
  quadrant: string;
  ring: string;
}

export interface RepoLensApi {
  getStats(filters?: {
    domain?: string;
    system?: string;
    team?: string;
  }): Promise<RepoLensStats>;
  getAnalysisList(params: {
    domain?: string;
    system?: string;
    team?: string;
    stack?: string;
    page?: number;
    limit?: number;
  }): Promise<AnalysisListResponse>;
  getAnalysis(componentRef: string): Promise<AnalysisResult | null>;
  getTechRadar(filters?: {
    domain?: string;
    system?: string;
    team?: string;
  }): Promise<TechRadarData>;
  getStacks(filters?: {
    domain?: string;
    system?: string;
    team?: string;
  }): Promise<StackAggregation[]>;
  getDomains(): Promise<string[]>;
  getSystems(): Promise<string[]>;
  getTeams(): Promise<string[]>;
  getExternalServicesSummary(filters?: {
    system?: string;
    domain?: string;
  }): Promise<ExternalServiceSummaryItem[]>;
  getComponentExternalServices(componentRef: string): Promise<ComponentExternalServiceItem[]>;
  triggerAnalysis(
    type: 'incremental' | 'full',
  ): Promise<{ runId: string; status: string }>;
}

export const repoLensApiRef = createApiRef<RepoLensApi>({
  id: 'plugin.repo-lens.api',
});

export class RepoLensClient implements RepoLensApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('repo-lens');
  }

  private buildQueryString(
    params: Record<string, string | number | undefined>,
  ): string {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '';
    return `?${entries
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&')}`;
  }

  async getStats(filters?: {
    domain?: string;
    system?: string;
    team?: string;
  }): Promise<RepoLensStats> {
    const baseUrl = await this.getBaseUrl();
    const query = this.buildQueryString(filters ?? {});
    const response = await this.fetchApi.fetch(`${baseUrl}/stats${query}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch stats: ${response.statusText}`);
    }
    return response.json();
  }

  async getAnalysisList(params: {
    domain?: string;
    system?: string;
    team?: string;
    stack?: string;
    page?: number;
    limit?: number;
  }): Promise<AnalysisListResponse> {
    const baseUrl = await this.getBaseUrl();
    const query = this.buildQueryString(params);
    const response = await this.fetchApi.fetch(`${baseUrl}/analysis${query}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch analysis list: ${response.statusText}`);
    }
    return response.json();
  }

  async getAnalysis(componentRef: string): Promise<AnalysisResult | null> {
    const baseUrl = await this.getBaseUrl();
    const encodedRef = encodeURIComponent(componentRef);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/analysis/${encodedRef}`,
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch analysis: ${response.statusText}`);
    }
    return response.json();
  }

  async getTechRadar(filters?: {
    domain?: string;
    system?: string;
    team?: string;
  }): Promise<TechRadarData> {
    const baseUrl = await this.getBaseUrl();
    const query = this.buildQueryString(filters ?? {});
    const response = await this.fetchApi.fetch(`${baseUrl}/tech-radar${query}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch tech radar: ${response.statusText}`);
    }
    return response.json();
  }

  async getStacks(filters?: {
    domain?: string;
    system?: string;
    team?: string;
  }): Promise<StackAggregation[]> {
    const baseUrl = await this.getBaseUrl();
    const query = this.buildQueryString(filters ?? {});
    const response = await this.fetchApi.fetch(`${baseUrl}/stacks${query}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch stacks: ${response.statusText}`);
    }
    const json = await response.json();
    return json.stacks;
  }

  async getDomains(): Promise<string[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/domains`);
    if (!response.ok) {
      throw new Error(`Failed to fetch domains: ${response.statusText}`);
    }
    const json = await response.json();
    return json.domains;
  }

  async getSystems(): Promise<string[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/systems`);
    if (!response.ok) {
      throw new Error(`Failed to fetch systems: ${response.statusText}`);
    }
    const json = await response.json();
    return json.systems;
  }

  async getTeams(): Promise<string[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/teams`);
    if (!response.ok) {
      throw new Error(`Failed to fetch teams: ${response.statusText}`);
    }
    const json = await response.json();
    return json.teams;
  }

  async getExternalServicesSummary(filters?: {
    system?: string;
    domain?: string;
  }): Promise<ExternalServiceSummaryItem[]> {
    const baseUrl = await this.getBaseUrl();
    const query = this.buildQueryString(filters ?? {});
    const response = await this.fetchApi.fetch(
      `${baseUrl}/external-services/summary${query}`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch external services summary: ${response.statusText}`,
      );
    }
    const json = await response.json();
    return json.services;
  }

  async getComponentExternalServices(componentRef: string): Promise<ComponentExternalServiceItem[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/external-services/by-component?componentRef=${encodeURIComponent(componentRef)}`,
    );
    if (response.status === 404) return [];
    if (!response.ok) {
      throw new Error(
        `Failed to fetch component external services: ${response.statusText}`,
      );
    }
    const json = await response.json();
    return json.externalServices ?? [];
  }

  async triggerAnalysis(
    type: 'incremental' | 'full',
  ): Promise<{ runId: string; status: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/trigger?type=${type}`,
      {
        method: 'POST',
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to trigger analysis: ${response.statusText}`);
    }
    return response.json();
  }
}
